import type { AutoSpinStore } from "../../autospin/AutoSpinStore";
import type { InteractionBlockStore } from "../../interaction-block/InteractionBlockStore";
import type { InteractionBlockReason } from "../../interaction-block/InteractionBlockTypes";
import type { AudioService } from "../../audio/ports/AudioService";
import type { HapticsService } from "../../haptics/HapticsService";
import type { SpinCoordinator } from "../../spin/SpinCoordinator";
import { HoldToSpinPolicy, type HoldToSpinTuning } from "./HoldToSpinPolicy";
import { IdleHintPolicy } from "./IdleHintPolicy";
import type { SpinButtonPort } from "./SpinButtonPort";
import { SpinInputSources, type SpinInputListener, type SpinInputWindow } from "./SpinInputSources";
import type { PresentationTaskRunner } from "@slot/presentation";

export interface SpinInputPresenterDeps {
  /** Operator setting: hold-to-spin (turbo chaining) allowed. */
  holdToSpinEnabled: boolean;
  autoSpinStore: AutoSpinStore;
  interactionBlockStore: InteractionBlockStore;
  audioService: AudioService;
  hapticsService: HapticsService;
  /** The chrome runner: hold and hint timers run in real seconds and freeze with the dev pause. */
  chromeTaskRunner: PresentationTaskRunner;
  tuning?: Partial<HoldToSpinTuning> & { idleHintDelaySeconds?: number };
  /** Test seam for the window listeners; `null` for none. */
  window?: SpinInputWindow | null;
  /** A round waiting for the player's Spin (decision 55): a tap on the spin face or the spacebar ends the wait. */
  playerSpins?: { readonly waiting: boolean; press(): boolean } | null;
}

/**
 * The one block a keyboard slam goes through: `feature_bonus`, the feature's own state, which covers
 * nothing. The bar's slam face and the grid tap slam through free spins, so Space does too (user,
 * 2026-09-28; review 2026-09-27 #29). Every other reason stands for a surface over the game (a page, a
 * modal, the purchase panel, the mobile menu and pickers, the big-win and trigger panels) and refuses
 * the key, as that surface stops the pointer; a reason added later refuses it too.
 */
const SLAM_THROUGH_BLOCKS: ReadonlySet<InteractionBlockReason> = new Set<InteractionBlockReason>(["feature_bonus"]);

/**
 * What spin input means: a pointer tap spins on a release over the spin face, the spacebar spins on
 * key down (or slams when a round is presenting); the slam button slams. A tap plays the cue and a
 * haptic, a held spin plays only the cue. `SpinInputSources` produces the intents, `HoldToSpinPolicy`
 * decides when a press is a hold and what a hold does, `IdleHintPolicy` owns the "hold to spin" hint.
 */
export class SpinInputPresenter implements SpinInputListener {
  private readonly sources: SpinInputSources;
  private readonly hold: HoldToSpinPolicy;
  private readonly hint: IdleHintPolicy;
  private unsubscribeSpin?: () => void;
  private unsubscribeAutoSpin?: () => void;
  private unsubscribeInteractionBlock?: () => void;

  public constructor(
    private readonly panel: SpinButtonPort,
    private readonly deps: SpinInputPresenterDeps,
    private readonly spinCoordinator: SpinCoordinator,
    private readonly onBeforeSpin?: () => void,
  ) {
    this.sources = new SpinInputSources(panel, this, deps.window);
    this.hint = new IdleHintPolicy({
      runner: deps.chromeTaskRunner,
      enabled: deps.holdToSpinEnabled,
      delaySeconds: deps.tuning?.idleHintDelaySeconds,
      canShow: () => {
        const snap = this.spinCoordinator.getSnapshot();
        // An offered gamble keeps the hint down, beyond Cocos: on portrait the caption and the Gamble
        // entry share one spot, and a held spin would give the offer up (decision 36).
        return !this.hold.isEngaged() && this.canUseSpinInput() && snap.canSpin && !snap.canSlamStop && !snap.canGamble;
      },
      setVisible: (visible) => this.panel.setHoldHintVisible(visible),
    });
    this.hold = new HoldToSpinPolicy({
      runner: deps.chromeTaskRunner,
      enabled: deps.holdToSpinEnabled,
      tuning: deps.tuning,
      coordinator: spinCoordinator,
      canUseInput: () => this.canUseSpinInput(),
      actions: {
        spinHeld: () => this.spin({ haptic: false }),
        slam: () => this.spinCoordinator.slamStop(),
        warmupStart: () => this.panel.playHoldWarmupVisual(),
        warmupStop: () => this.panel.stopHoldWarmupVisual(),
        holdStarted: () => this.hint.hide(),
        holdStopped: () => this.registerUserActivity(),
      },
    });
  }

  public start(): void {
    this.panel.setHoldHintVisible(false);
    this.panel.resetHoldWarmupVisualImmediate();
    this.sources.start();
    this.hold.start();
    this.unsubscribeSpin = this.spinCoordinator.subscribe(() => this.hint.refresh());
    this.unsubscribeAutoSpin = this.deps.autoSpinStore.subscribe(() => this.handleExternalStateChange());
    this.unsubscribeInteractionBlock = this.deps.interactionBlockStore.subscribe(() => this.handleExternalStateChange());
  }

  public stop(): void {
    this.sources.stop();
    this.hold.stop();
    this.hint.stop();
    this.unsubscribeSpin?.();
    this.unsubscribeSpin = undefined;
    this.unsubscribeAutoSpin?.();
    this.unsubscribeAutoSpin = undefined;
    this.unsubscribeInteractionBlock?.();
    this.unsubscribeInteractionBlock = undefined;
    this.panel.resetHoldWarmupVisualImmediate();
  }

  public onSpinBecameAvailable(): void {
    if (this.hold.isEngaged() || !this.canUseSpinInput()) return;
    this.hint.arm();
  }

  public onSpinBecameUnavailable(): void {
    this.hint.hide();
  }

  public slamClick(): void {
    if (!this.spinCoordinator.getSnapshot().canSlamStop) return;
    this.registerUserActivity();
    // SFX + haptics fire from the coordinator's onSlamStop callback, gated on work actually cancelled.
    this.spinCoordinator.slamStop();
  }

  public pointerDown(): void {
    this.registerUserActivity();
    this.hold.pressStart("pointer", this.spinCoordinator.getSnapshot().canSpin);
  }

  /**
   * A pointer tap acts on a release over the spin face; a press that became a hold, or ended
   * anywhere else, releases into nothing.
   */
  public pointerUp(onButton: boolean): void {
    const { wasPressed, holdReleased } = this.hold.pressEnd("pointer");
    if (!wasPressed && !holdReleased) return;
    this.registerUserActivity();
    if (holdReleased || !onButton) return;
    if (this.deps.playerSpins?.waiting) {
      this.pressPlayerSpin();
      return;
    }
    if (!this.canUseSpinInput() || !this.spinCoordinator.getSnapshot().canSpin) return;
    this.spin({ haptic: true });
  }

  /** The spacebar acts on key down: slam while presenting, spin while idle. */
  public keyDown(): void {
    this.registerUserActivity();
    if (this.hold.isPressed("keyboard")) return;
    const snap = this.spinCoordinator.getSnapshot();
    const pressedFromIdle = snap.canSpin;
    if (this.deps.playerSpins?.waiting) {
      // A round waiting for the player's Spin: the key is that Spin, as the face's tap is (decision 55).
      this.pressPlayerSpin();
    } else if (snap.canSlamStop) {
      // A slam is input like any other, so a block refuses it (user, 2026-09-21), all but
      // SLAM_THROUGH_BLOCKS (user, 2026-09-28). The DOM pages stop the pointer but never the keyboard,
      // and the overlay panels sit above the slam surface; without this a press behind an open info page
      // or modal cut the round short with a slam cue. The panels keep their own any-key skip through
      // `overlayInput`, so a press under `big_win` or `feature_trigger_panel` still dismisses the panel.
      // Autospin deliberately does NOT gate here (`canUseSpinInput` also excludes a running autospin):
      // a slam must work mid-run.
      const refused = this.deps.interactionBlockStore.getSnapshot().reasons.some((reason) => !SLAM_THROUGH_BLOCKS.has(reason));
      if (!refused) this.spinCoordinator.slamStop();
    } else if (this.canUseSpinInput() && snap.canSpin) {
      this.spin({ haptic: true });
    }
    this.hold.pressStart("keyboard", pressedFromIdle);
  }

  public keyUp(): void {
    const { wasPressed, holdReleased } = this.hold.pressEnd("keyboard");
    if (!wasPressed && !holdReleased) return;
    this.registerUserActivity();
  }

  /**
   * The player's Spin for a round waiting on it (decision 55): the round goes on, with the spin's cue and haptic. A block
   * other than the feature's own (a page, a modal, a menu over the game) refuses it, as it refuses a slam.
   */
  private pressPlayerSpin(): void {
    if (this.deps.interactionBlockStore.getSnapshot().reasons.some((reason) => !SLAM_THROUGH_BLOCKS.has(reason))) return;
    this.hint.hide();
    this.onBeforeSpin?.();
    if (!this.deps.playerSpins?.press()) return;
    this.deps.audioService.presentation.emit({ id: "spin.start" });
    this.deps.hapticsService.play("soft");
  }

  private spin(input: { haptic: boolean }): void {
    this.hint.hide();
    this.onBeforeSpin?.();
    this.deps.audioService.presentation.emit({ id: "spin.start" });
    if (input.haptic) this.deps.hapticsService.play("soft");
    void this.spinCoordinator.requestSpin();
  }

  private canUseSpinInput(): boolean {
    return !this.deps.autoSpinStore.getSnapshot().running && !this.deps.interactionBlockStore.isBlocked();
  }

  private handleExternalStateChange(): void {
    this.hold.externalStateChanged();
    this.registerUserActivity();
  }

  /** Any input: the hint hides now and re-arms only while a spin is possible. */
  private registerUserActivity(): void {
    if (this.canUseSpinInput() && this.spinCoordinator.getSnapshot().canSpin) this.hint.arm();
    else this.hint.hide();
  }
}
